// Unpublished Work © 2023 Deere & Company.
import { Theme } from "@deere/fuel-react";
import { GridClasses } from "@mui/x-data-grid-pro";

const columnsHeaderSx = (theme: Theme) => ({
  fontWeight: theme.tokens.FuelFontWeightSemibold,
  fontSize: theme.tokens.FuelFontSizeHeading6,
});

const dataGridSx = (
  theme: Theme,
  gridClasses: Record<keyof GridClasses, string>,
  hasRows: boolean,
  print: boolean = false
) => ({
  borderColor: theme.tokens.FuelColorBorderLight,
  [`.${gridClasses.main}`]: {
    overflow: print ? "visible" : "auto",
  },
  [`${gridClasses.withBorderColor}`]: {
    borderColor: "none",
  },
  [`& .${gridClasses.row}`]: {
    "&:first-child": {
      border: 0,
    },
  },
  [`.${gridClasses.cell}`]: {
    display: "flex",
    alignItems: "center",
    borderColor: theme.tokens.FuelColorBorderLight,
  },
  [`.${gridClasses.cell} a svg`]: {
    marginTop: "auto",
    marginBottom: "auto",
  },
  [`.${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
    outline: "none",
  },
  [`.MuiDataGrid-cell.MuiDataGrid-cell--editing`]: {
    boxShadow: "none !important",
    padding: "10px",
    outline: "none !important",
    backgroundColor: "transparent !important",
  },
  [`.MuiDataGrid-pinnedColumns`]: {
    backgroundColor: "white !important",
  },
  [`.${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
    {
      outline: "none",
    },
  [`.${gridClasses.columnSeparator}`]: {
    visibility: "visible",
    color: theme.tokens.FuelColorBorderLight,
  },
  [`.${gridClasses.virtualScroller}`]: {
    overflowX: print ? "visible" : "auto",
    overflowY: print ? "visible" : "hidden",
    height: hasRows ? "inherit" : "60vh",
  },
  "& .MuiDataGrid-columnHeadersInner--scrollable": {
    transform: print ? "inherit !important" : "auto",
  },
  [`& .data-row-theme--Child`]: {
    border: 0,
    borderLeft: 0,
    borderBottom: 0,
    [`& button`]: {
      display: "none",
    },
  },
  [`& .MuiDataGrid-menuIcon`]: {
    width: "auto",
    visibility: "visible",
  },
  [`& .MuiDivider-root`]: {
    display: "none",
  },
  [`& .data-row-theme--Unseen`]: {
    boxShadow: `2px 0 0 ${theme.tokens.FuelColorBrandPrimary} inset`,
    fontWeight: `${theme.tokens.FuelFontWeightSemibold}`,
  },
  [`.MuiDataGrid-pinnedColumns > .data-row-theme--Unseen`]: {
    boxShadow: `0 0 0 0 !important`,
  },
  [`.MuiDataGrid-pinnedColumnHeaders`]: {
    backgroundColor: "white !important",
    top: "unset",
    border: 0,
  },
  [`.MuiDataGrid-columnHeaderTitleContainer`]: {
    whiteSpace: "initial",
  },
  [`.${gridClasses["cell--pinnedLeft"]}, .${gridClasses["columnHeader--pinnedLeft"]},
    .${gridClasses["columnHeader--pinnedRight"]}, .${gridClasses["cell--pinnedRight"]}`]:
    {
      backgroundColor: theme.tokens.FuelColorBackgroundWhite,
    },
  [`& .${gridClasses.columnHeaders} > div[role="row"]`]: {
    backgroundColor: theme.tokens.FuelColorBackgroundWhite,
    borderRadius: "0.5rem 0.5rem 0 0",
    borderColor: theme.tokens.FuelColorBorderLight,
  },
  [`.${gridClasses["columnHeader--last"]}`]: {
    borderLeft: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  },
  [`.${gridClasses["cell--pinnedRight"]}`]: {
    boxShadow:
      "rgba(27, 27, 27, 0.05) 0px 0px 5px 0px, rgba(27, 27, 27, 0.15) 0px 1px 2px 0px",
  },
  "--DataGrid-overlayHeight": "500px",
  [`.${gridClasses["scrollbar--horizontal"]}`]: {
    zIndex: 0,
  },
  [`div.MuiDataGrid-topContainer.MuiDataGrid-container--top.css-1oudwrl:: after`]:
    {
      backgroundColor: theme.tokens.FuelColorBorderLight,
    },
  ".css-1t3gfz6-MuiGrid-root": {
    paddingLeft: "14px",
  },
});

export { columnsHeaderSx, dataGridSx };
